import React, { useState, useEffect, useContext, useCallback, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFToggle from 'src/components/controls/tempRHFControls/RHFToggle';
import actions from 'src/actions';
import LeaseholdDocs from '../LeasingComponents/LeaseholdDocs';
import DialogList from 'src/dialogs';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import PropertyUtil from 'src/util/PropertyUtil';
import Ownership from 'src/models/tract_detail/Ownership';
import Owner from 'src/models/tract_detail/Owner';
import WIOwnership from 'src/models/tract_detail/WIOwnership';
import useFetch from 'use-http';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import Lease from 'src/models/leasehold/Lease';
import LeaseFormData from 'src/models/leasehold/LeaseFormData';
import AddOwnership from './AddOwnership';
import AddOwner from 'src/models/tract_detail/add_ownership_revised/AddOwner';
import { IRootState } from 'src/reducers';
import { push, replace } from 'connected-react-router';
import AddWIOwnershipData from 'src/models/tract_detail/add_ownership_revised/AddWIOwnership';
import { ODataOptions, ODataFilter, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';
import WorkingInterestList from './WorkingInterestList';
import moment from 'moment';
import OwnerUtil from 'src/util/OwnerUtil';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFLeaseClause from 'src/components/controls/tempRHFControls/RHFLeaseClause';
import LeaseInfoDeleteDialog from './LeaseInfoDeleteDialog';
import LeaseGenerationTractDetail from 'src/models/leasing/LeaseGenerationTractDetail';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import * as Constants from 'src/components/common/Constants';
import LeaseholdModel from 'src/models/leasehold/Leasehold';
import { ITableConfig } from 'src/components/common/table';
import OwnerSearchResult from 'src/models/OwnerSearchResult';
import PagedResultSet from 'src/models/common/PagedResultSet';
import ErrorResponse from 'src/models/ErrorResponse';

const LeaseInfo = () => {
    const { register, handleSubmit, errors, reset, getValues, watch, setValue, formState } = useForm<LeaseFormData>();
    const dispatch = useDispatch();
    const [parentOwnershipId, setParentOwnershipId] = useState(undefined);
    const dialogContext = React.useContext<dialogCreators>(DialogContext);
    const [copyLeaseholdClicked, setCopyLeaseholdClicked] = useState(false);
    const [leaseCreateMode, setLeaseCreateMode] = useState(false);
    const [sourceSelected, setSourceSelected] = useState(false);
    const watchExpirationCalcFields = watch(['effectiveDate', 'leasehold.primaryTerm', 'leasehold.primaryTermUnits']);
    const [leaseData, setLeaseData] = useState<Lease>(undefined);
    const [leaseholdIdData, setLeaseholdIdData] = useState<Array<number>>(undefined);
    const { get, response, put, post, del, request } = useFetch();
    const appContext = useContext<IAppContextState>(AppContext);
    const [currentLeasehold, setCurrentLeasehold] = useState(-1);
    const [initialLeaseholdId, setInitialLeaseholdId] = useState(-1);
    const { tract } = useSelector((state: IRootState) => state);
    const { state: selectState } = useSelector((state: IRootState) => state);
    const { owner: selectOwner } = useSelector((state: IRootState) => state);
    const { phoneNumberType: selectPhoneNumberTypes } = useSelector((state: IRootState) => state);
    const { isDirty } = formState;
    const [selectedWIOwnershipId, setSelectedWIOwnershipId] = useState(undefined);
    const [isMemo, setIsMemo] = useState<boolean>(false);
    const [ownerTracts, setOwnerTracts] = useState<Array<LeaseGenerationTractDetail>>([]);
    const [royalty, setRoyalty] = useState(0);
    const [ownerSearchDataOptions, setOwnerSearchDataOptions] = useState<ODataOptions>(undefined);
    const [fetchNextPage, setFetchNextPage] = useState<boolean>(true);
    // const [ownerList, setOwnerList] = useState<PagedResultSet<OwnerSearchResult>>(new PagedResultSet<OwnerSearchResult>());

    const getLeaseData = useCallback(
        async (leaseId: number, leaseHoldIndex: number = 0) => {
            const result = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/${leaseId}/leasehold-index/${leaseHoldIndex}`);
            if (response.ok) {
                setLeaseData(result);
                setLeaseholdIdData(result?.leaseHoldIds);
            } else {
                window.alert('Get Lease Data call failed');
            }
        },
        [appContext.clientNumber, appContext.projectNumber, get, response.ok]
    );

    const updateLeaseData = async (data: Lease) => {
        let leaseholdId = await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/${appContext.leaseId}`, data);

        if (response.ok) {
            let currentPath = window.location.pathname.split('/');
            currentPath.splice(currentPath.length - 1, 1);
    
            dispatch(
                push(`${currentPath.join('/')}/${leaseholdId}`)
            );
            getLeaseData(appContext.leaseId, currentLeasehold + 1);
        } else {
            window.alert('call failed');
        }
    };

    const createLeaseData = async (data: Lease) => {
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 3, 3);

        const result = await post(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases`, data);
        if (response.ok) {
            dispatch(
                replace(
                    `${currentPath.join('/')}/${result.leaseId}/leaseholds/${result.leaseholdId}`
                )
            );
            if (currentLeasehold === -1) {
                setCurrentLeasehold(0);
            }
        } else {
            window.alert('Create Lease Data failed');
        }
        setLeaseCreateMode(false);
    };

    const deleteLeaseData = async () => {
        console.log('trying to delete');
        const result = await request.delete(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/${appContext.leaseId}`
        );
        if (response.ok) {
            dispatch(
                replace(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}`
                )
            );
            console.log(result);
            // getLeaseData(result.leaseId);
        } else {
            window.alert('Delete Lease Data failed');
        }
    };

    const onUpdateWINote = async (ownership: Ownership) => {
        await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/${appContext.leaseId}/leaseholds/${appContext.leaseholdId}/notes`,
            ownership
        );

        if (response.ok) {
            dispatch(actions.layout.hideDialog());
            getLeaseData(appContext.leaseId);
        } else {
            window.alert(`update wi note failed. note: ${ownership.note}`);
        }
    };

    const handleExpirationChange = (e: React.FormEvent<HTMLInputElement> | React.FormEvent<HTMLSelectElement>) => {
        calcExpirationDate();
    };

    const initializeOwnerSearchDataOptions = (newOptions: ODataOptions) => {
        const tableConfig: ITableConfig = {
            rowCount: 50,
            idField: 'Id',
            pageNumber: 1,
            columns: []
        };
        let newDataOptions = ODataOptions.from(tableConfig).addOrderBy('Name', ODataOrderByDirection.Ascending);
        newDataOptions.setIsNextpage(true);
        dispatch(actions.tract.initializeOwnerList(tableConfig));
        dispatch(actions.tract.clearSearchResults());
        setOwnerSearchDataOptions(!newOptions ? newDataOptions : newOptions);
    };

    useEffect(() => {
        initializeOwnerSearchDataOptions(null);
        let currentPath = window.location.pathname.split('/');
        let leaseIdIndex = currentPath?.findIndex((lh) => lh === 'leases') + 1;
        let leaseId = Number(currentPath[leaseIdIndex]);
        if (leaseId > 0) {
            getLeaseData(leaseId, 0);
        } else {
            setLeaseCreateMode(true);
        }
    }, []);

    useEffect(() => {
        if (!!ownerSearchDataOptions && ownerSearchDataOptions.getPageNumber() < 2) {
            dispatch(actions.tract.getSearchPagedOwners(appContext.clientNumber, appContext.projectNumber, ownerSearchDataOptions));
        }
    }, [ownerSearchDataOptions]);

    useEffect(() => {
        if (initialLeaseholdId === -1) {
            setInitialLeaseholdId(appContext.leaseholdId);
        }
    }, [initialLeaseholdId]);

    useEffect(() => {
        if (!!tract?.pagedSearchOwnersResults && ((tract?.pagedSearchOwnersResults as unknown as ErrorResponse)?.isError) !== true) {
            setFetchNextPage(tract?.pagedSearchOwnersResults.pageNumber * tract?.pagedSearchOwnersResults.pageSize < tract?.pagedSearchOwnersResults.totalRecords);
        }
    }, [tract?.pagedSearchOwnersResults]);

    useEffect(() => {
        if (leaseholdIdData !== undefined) {
            let initiallySelectedLeaseHoldIndex = -1;
            if (appContext.leaseholdId > 0 && leaseholdIdData !== null) {
                initiallySelectedLeaseHoldIndex = leaseholdIdData?.findIndex((lh) => lh === appContext.leaseholdId);
            }

            if ( initiallySelectedLeaseHoldIndex > -1) {
                setCurrentLeasehold(initiallySelectedLeaseHoldIndex);
            } else if (appContext.leaseholdId === 0) {
                setCurrentLeasehold(-1);
            }
        }
    }, [leaseholdIdData]);

    useEffect(() => {
        if (currentLeasehold !== -1) {
            getLeaseData(appContext.leaseId, currentLeasehold + 1);
        }
    }, [currentLeasehold]);

    useEffect(() => {
        if (leaseData !== undefined && initialLeaseholdId !== -1) {
            setIsMemo(leaseData?.isFiledByMemo);
            if (leaseData?.id > 0) {
                reset(prepData(leaseData));
            }
            setRoyalty(leaseData?.leaseholds?.[Constants.leaseInfoPagedefaultLeaseholdIndex]?.royalty);
    }
    }, [leaseData]);

    const prepData = useCallback(
        (roughLeaseData: Lease) => {
            setCopyLeaseholdClicked(false);
            let leaseHoldData = currentLeasehold !== -1 ? roughLeaseData.leaseholds?.[Constants.leaseInfoPagedefaultLeaseholdIndex] : new LeaseholdModel();
            const formData: LeaseFormData = {
                id: roughLeaseData.id,
                volume: roughLeaseData.volume,
                page: roughLeaseData.page,
                recordedDate: roughLeaseData.recordedDate ? (moment(roughLeaseData.recordedDate).format('YYYY-MM-DD') as unknown as Date) : null,
                leaseDate: roughLeaseData.leaseDate ? (moment(roughLeaseData.leaseDate).format('YYYY-MM-DD') as unknown as Date) : null,
                effectiveDate:
                    roughLeaseData.effectiveDate !== undefined || null
                        ? (new Date(roughLeaseData.effectiveDate).toISOString().split('T')[0] as unknown as Date)
                        : null,
                originalLessee: roughLeaseData.originalLessee,
                originalLessor: roughLeaseData.originalLessor,
                ownersOnDocument: roughLeaseData.ownersOnDocument,
                lesseeName: roughLeaseData.lesseeName,
                leasedBy: roughLeaseData.leasedBy,
                fedStateLeaseNumber: roughLeaseData.fedStateLeaseNumber,
                isFiledByMemo: roughLeaseData.isFiledByMemo,
                recordTypeId: roughLeaseData.recordTypeId,
                instrumentNumber: roughLeaseData.instrumentNumber,
                isRentalLease: roughLeaseData.isRentalLease,
                rentalPrice: roughLeaseData.rentalPrice,
                rentalPriceTypeId: roughLeaseData.rentalPriceTypeId,
                originalGross: roughLeaseData.originalGross,
                leaseNumber: roughLeaseData.leaseNumber,
                legalDescription: roughLeaseData.legalDescription,
                bonusAmount: roughLeaseData.bonusAmount,
                bonusUnits: roughLeaseData.bonusUnits,
                totalBonus: roughLeaseData.totalBonus,

                leasehold: {
                    ...leaseHoldData,
                    expirationDate: roughLeaseData.leaseholds?.[Constants.leaseInfoPagedefaultLeaseholdIndex]?.expirationDate
                        ? (new Date(roughLeaseData.leaseholds?.[Constants.leaseInfoPagedefaultLeaseholdIndex]?.expirationDate).toISOString().split('T')[0] as unknown as Date)
                        : null
                },
            };

            // setup all watches here

            return formData;
        },
        [currentLeasehold]
    );

    // Disable Copy Leasehold button until Source Leasehold is selected
    useEffect(() => {
        setSourceSelected(false);
    }, [copyLeaseholdClicked]);

    // Enable Copy Leasehold button when the selected Source changes from default
    const handleSourceSelected = (e: FormEvent<HTMLSelectElement>) => {
        const selectedItem = e.target as HTMLSelectElement;

        if (selectedItem.value !== '-- Select --') {
            setSourceSelected(true);
            setCopyLeaseholdClicked(true);
        }
    };

    const getNewOwnerList = (): PagedResultSet<OwnerSearchResult> => {
        let newOwnerList = new PagedResultSet<OwnerSearchResult>();
        if (!!tract?.pagedSearchOwnersResults?.items) {
            newOwnerList.items = [];
            newOwnerList.items = [...tract?.pagedSearchOwnersResults?.items];
        }
        return newOwnerList;
    };

    const getScrollStatus = (divElement: HTMLDivElement) => {
        if (divElement) {
            const scrollTop = divElement.scrollTop % 1 > .9 ? divElement.scrollTop : Math.round(divElement.scrollTop);
            const scrollHeight = divElement.scrollHeight;
            const clientHeight = divElement.clientHeight;

            // Check if reached the end of the list
            if (scrollTop + clientHeight >= scrollHeight) {
                incrementOwners();
                console.log('end of scroll YES');
            } else {
                console.log('end of scroll NO');
            }
        }
    };

    const incrementOwners = () => {
        if (!!fetchNextPage) {
            let newSearchOptions = _.cloneDeep(ownerSearchDataOptions);
            newSearchOptions.incrementPageNumber();
            dispatch(actions.tract.incrementOwnersPage(appContext.clientNumber, appContext.projectNumber, newSearchOptions));
            setOwnerSearchDataOptions(newSearchOptions);
        }
    };

    const calcExpirationDate = () => {
        const term = watchExpirationCalcFields['leasehold.primaryTerm']
            ? parseInt(watchExpirationCalcFields['leasehold.primaryTerm'], 10)
            : undefined;
        const termUnit = watchExpirationCalcFields['leasehold.primaryTermUnits']
            ? watchExpirationCalcFields['leasehold.primaryTermUnits']
            : undefined;
        const effectiveDate = watchExpirationCalcFields.effectiveDate
            ? new Date((watchExpirationCalcFields?.effectiveDate as Date)?.toString()) || undefined
            : undefined;

        if (term && termUnit && effectiveDate && !isNaN(term) && termUnit && effectiveDate instanceof Date) {
            switch (termUnit) {
                case 'years':
                    setValue(
                        'leasehold.expirationDate',
                        new Date(effectiveDate.setFullYear(effectiveDate.getFullYear() + term)).toISOString().split('T')[0]
                    );
                    break;

                case 'months':
                    setValue(
                        'leasehold.expirationDate',
                        new Date(effectiveDate.setMonth(effectiveDate.getMonth() + term)).toISOString().split('T')[0]
                    );
                    break;

                case 'days':
                    setValue('leasehold.expirationDate', new Date(effectiveDate.setDate(effectiveDate.getDate() + term)).toISOString().split('T')[0]);
                    break;

                default:
                    break;
            }
        } else {
            setValue('leasehold.expirationDate', '');
        }
    };

    const onSubmit = (data: Lease) => {
        data = finalizeData(data);
        if (leaseCreateMode === false) {
            let currentPath = window.location.pathname.split('/');
            let leaseIdIndex = currentPath?.findIndex((lh) => lh === 'leases') + 1;
            data.id = Number(currentPath[leaseIdIndex]);
            data.leasehold.leaseId = Number(currentPath[leaseIdIndex]);
        }

        if (data.id > 0) {
            updateLeaseData(data);
        } else {
            data.isInHand = true;
            data.inHandDate = new Date();
            data.isCommitted = true;
            data.committedDate = new Date();
            createLeaseData(data);
        }
    };

    const finalizeData = (data: Lease) => {
        data.id = leaseData?.id ? leaseData.id : 0;
        data.volume = data.volume ? data.volume : '';
        data.page = data.page ? data.page : '';
        data.recordedDate = data.recordedDate ? (moment(data.recordedDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.leaseDate = data.leaseDate
            ? (moment(data.leaseDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.effectiveDate = data.effectiveDate
            ? (moment(data.effectiveDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.lesseeName = data.lesseeName ? data.lesseeName : '';
        data.ownersOnDocument = data.ownersOnDocument ? data.ownersOnDocument : '';
        data.originalGross = data.originalGross ? data.originalGross : 0;
        data.fedStateLeaseNumber = data.fedStateLeaseNumber ? data.fedStateLeaseNumber : '';
        data.isFiledByMemo = parseBool(data.isFiledByMemo + '');
        data.recordTypeId = data.recordTypeId ? data.recordTypeId : 0;
        data.instrumentNumber = data.instrumentNumber ? data.instrumentNumber : '';
        data.isRentalLease = data.isRentalLease ? data.isRentalLease : null;
        data.rentalPrice = data.rentalPrice ? data.rentalPrice : 0;
        data.rentalPriceTypeId = data.rentalPriceTypeId ? data.rentalPriceTypeId : 0;
        data.leaseNumber = data.leaseNumber ? data.leaseNumber : '';
        data.originalGross = data.originalGross ? parseFloat(data.originalGross + '') : 0;
        data.legalDescription = data.legalDescription ? data.legalDescription : '';
        data.bonusUnits = data.bonusUnits ? data.bonusUnits : '';
        data.bonusAmount = data.bonusAmount ? parseFloat(data.bonusAmount + '') : 0;
        data.totalBonus = data.totalBonus ? parseFloat(data.totalBonus + '') : 0;
        // data.leasehold = undefined;
        data.leasehold = {
            id: appContext.leaseholdId ? appContext.leaseholdId : 0,
            projectId: data.leasehold.projectId,
            royalty: data.leasehold.royalty ? parseFloat(data.leasehold.royalty + '') : null,
            fedStateLeaseNumber: data.leasehold.fedStateLeaseNumber ? data.leasehold.fedStateLeaseNumber : '',
            leaseDate: data.leasehold.leaseDate
                ? (moment(data.leasehold.leaseDate).format('YYYY-MM-DD') as unknown as Date)
                : (moment().format('YYYY-MM-DD') as unknown as Date),
            effectiveDate: data.leasehold.effectiveDate
                ? (moment(data.leasehold.effectiveDate).format('YYYY-MM-DD') as unknown as Date)
                : (moment().format('YYYY-MM-DD') as unknown as Date),
            poolingOrderNumber: data.leasehold.poolingOrderNumber ? data.leasehold.poolingOrderNumber : '',
            renewalOption: data.leasehold.renewalOption ? parseBool(data.leasehold.renewalOption + '') : null,
            optionTerm: data.leasehold.optionTerm ? parseInt(data.leasehold.optionTerm + '', 10) : 0,
            optionTermUnits: data.leasehold.optionTermUnits ? data.leasehold.optionTermUnits : null,
            optionPricePerAcre: data.leasehold.optionPricePerAcre ? data.leasehold.optionPricePerAcre : '',
            optionExercised: data.leasehold.optionExercised ? parseBool(data.leasehold.optionExercised + '') : null,
            filedByMemo: data.leasehold.filedByMemo ? parseBool(data.leasehold.filedByMemo + '') : null,
            recordTypeId: data.leasehold.recordTypeId ? parseInt(data.leasehold.recordTypeId + '', 10) : 0,
            originalLessee: data.leasehold.originalLessee ? data.leasehold.originalLessee : '',
            originalLessor: data.leasehold.originalLessor ? data.leasehold.originalLessor : '',
            hbp: parseBool(data.leasehold.hbp + ''),
            operator: data.leasehold.operator ? data.leasehold.operator : '',
            wellName: data.leasehold.wellName ? data.leasehold.wellName : '',
            hbu: parseBool(data.leasehold.hbu + ''),
            apiNumber: data.leasehold.apiNumber ? data.leasehold.apiNumber : null,
            unitName: data.leasehold.unitName ? data.leasehold.unitName : '',
            fieldName: data.leasehold.fieldName ? data.leasehold.fieldName : '',
            totalBonus: data.leasehold.totalBonus ? parseFloat(data.leasehold.totalBonus + '') : 0,
            optionPerAcre: data.leasehold.optionPerAcre ? parseFloat(data.leasehold.optionPerAcre + '') : 0,
            leaseProgramNotes: data.leasehold.leaseProgramNotes ? data.leasehold.leaseProgramNotes : '',
            isTopLease: parseBool(leaseData?.leaseholds[0]?.isTopLease + ''),
            isHistoricalLease: data.leasehold.isActiveLease ? false : true,
            isActiveLease: data.leasehold.isActiveLease ? data.leasehold.isActiveLease : false,
            bonusPerAcre: data.leasehold.bonusPerAcre ? parseFloat(data.leasehold.bonusPerAcre + '') : 0,
            ownershipLeaseholdXRefId: data.leasehold.ownershipLeaseholdXRefId ? data.leasehold.ownershipLeaseholdXRefId : 0,
            ownershipId: data.leasehold.ownershipId ? parseInt(data.leasehold.ownershipId + '', 10) : appContext.ownershipId,
            primaryTerm: data.leasehold.primaryTerm ? parseInt(data.leasehold.primaryTerm + '', 10) : 0,
            primaryTermUnits: data.leasehold.primaryTermUnits ? data.leasehold.primaryTermUnits : null,
            notes: data.leasehold.notes ? data.leasehold.notes : '',
            instrumentNumber: data.leasehold.instrumentNumber ? data.leasehold.instrumentNumber : '',
            lessorAKA: data.leasehold.lessorAKA ? data.leasehold.lessorAKA : '',
            leaseholdClauseId: data.leasehold.leaseholdClauseId ? parseInt(data.leasehold.leaseholdClauseId + '', 10) : 0,
            leaseId: data.id ? data.id : 0,
            book: data.leasehold.book ? data.leasehold.book : '',
            page: data.leasehold.page ? data.leasehold.page : '',
            expirationDate: data.leasehold.expirationDate
                ? (moment(data.leasehold.expirationDate).format('YYYY-MM-DD') as unknown as Date)
                : (moment().format('YYYY-MM-DD') as unknown as Date),
            rentalLease: data.leasehold.rentalLease ? parseBool(data.leasehold.rentalLease + '') : null,
            rentalprice: data.leasehold.rentalprice ? parseFloat(data.leasehold.rentalprice + '') : 0,
            priceTypeId: data.leasehold.priceTypeId ? parseInt(data.leasehold.priceTypeId + '', 10) : 0,
            isRentalLease: data.leasehold.isRentalLease ? parseBool(data.leasehold.isRentalLease + '') : null,
            status: data.leasehold.status ? data.leasehold.status : '',
            mineralOwner: data.leasehold.mineralOwner ? data.leasehold.mineralOwner : null,
            optionBonusAmount: data.leasehold.optionBonusAmount ? parseFloat(data.leasehold.optionBonusAmount + '') : 0,
            optionTotalBonus: data.leasehold.optionTotalBonus ? parseFloat(data.leasehold.optionTotalBonus + '') : 0,
            optionBonusAmountUnits: data.leasehold.optionBonusAmountUnits ? data.leasehold.optionBonusAmountUnits : null,
            sourceLeaseholdId: parseInt(getValues('sourceLeaseholdId') + '', 10) || 0,

            leaseholdClause: {
                id: leaseData?.leaseholds?.[0]?.leaseholdClause?.id
                    ? leaseData?.leaseholds?.[0]?.leaseholdClause?.id
                    : 0,
                leasePoolingProvisions: data.leasehold?.leaseholdClause?.leasePoolingProvisions
                    ? parseInt(data.leasehold?.leaseholdClause?.leasePoolingProvisions + '', 10)
                    : 0,
                oilAcres: data.leasehold?.leaseholdClause?.oilAcres ? parseFloat(data.leasehold?.leaseholdClause?.oilAcres + '') : 0,
                gasAcres: data.leasehold?.leaseholdClause?.gasAcres ? parseFloat(data.leasehold?.leaseholdClause?.gasAcres + '') : 0,
                depthPugh: parseBool(data.leasehold?.leaseholdClause?.depthPugh + ''),
                pugh: parseBool(data.leasehold?.leaseholdClause?.pugh + ''),
                depthExclusion: parseBool(data.leasehold?.leaseholdClause?.depthExclusion + ''),
                continuingDevelopment: parseBool(data.leasehold?.leaseholdClause?.continuingDevelopment + ''),
                shutIn: parseBool(data.leasehold?.leaseholdClause?.shutIn + ''),
                surfaceRestriction: parseBool(data.leasehold?.leaseholdClause?.surfaceRestriction + ''),
                favoredNation: parseBool(data.leasehold?.leaseholdClause?.favoredNation + ''),
                nonDrilling: parseBool(data.leasehold?.leaseholdClause?.nonDrilling + ''),
                topLeaseLanguage: parseBool(data.leasehold?.leaseholdClause?.topLeaseLanguage + ''),
                netProceedsLease: parseBool(data.leasehold?.leaseholdClause?.netProceedsLease + ''),
                costFreeRoyalty: parseBool(data.leasehold?.leaseholdClause?.costFreeRoyalty + ''),
                consentToAssign: parseBool(data.leasehold?.leaseholdClause?.consentToAssign + ''),
                noticeOfAssign: parseBool(data.leasehold?.leaseholdClause?.noticeOfAssign + ''),
                poolingProvision: parseBool(data.leasehold?.leaseholdClause?.poolingProvision + ''),
                other: parseBool(data.leasehold?.leaseholdClause?.other + ''),

                depthPughDescription: data.leasehold?.leaseholdClause?.depthPughDescription
                    ? data.leasehold?.leaseholdClause?.depthPughDescription
                    : '',
                pughDescription: data.leasehold?.leaseholdClause?.pughDescription ? data.leasehold?.leaseholdClause?.pughDescription : '',
                depthExclusionDescription: data.leasehold?.leaseholdClause?.depthExclusionDescription
                    ? data.leasehold?.leaseholdClause?.depthExclusionDescription
                    : '',
                continuingDevelopmentDescription: data.leasehold?.leaseholdClause?.continuingDevelopmentDescription
                    ? data.leasehold?.leaseholdClause?.continuingDevelopmentDescription
                    : '',
                shutInDescription: data.leasehold?.leaseholdClause?.shutInDescription ? data.leasehold?.leaseholdClause?.shutInDescription : '',
                surfaceRestrictionDescription: data.leasehold?.leaseholdClause?.surfaceRestrictionDescription
                    ? data.leasehold?.leaseholdClause?.surfaceRestrictionDescription
                    : '',
                favoredNationDescription: data.leasehold?.leaseholdClause?.favoredNationDescription
                    ? data.leasehold?.leaseholdClause?.favoredNationDescription
                    : '',
                nonDrillingDescription: data.leasehold?.leaseholdClause?.nonDrillingDescription
                    ? data.leasehold?.leaseholdClause?.nonDrillingDescription
                    : '',
                topLeaseLanguageDescription: data.leasehold?.leaseholdClause?.topLeaseLanguageDescription
                    ? data.leasehold?.leaseholdClause?.topLeaseLanguageDescription
                    : '',
                netProceedsLeaseDescription: data.leasehold?.leaseholdClause?.netProceedsLeaseDescription
                    ? data.leasehold?.leaseholdClause?.netProceedsLeaseDescription
                    : '',
                costFreeRoyaltyDescription: data.leasehold?.leaseholdClause?.costFreeRoyaltyDescription
                    ? data.leasehold?.leaseholdClause?.costFreeRoyaltyDescription
                    : '',
                consentToAssignDescription: data.leasehold?.leaseholdClause?.consentToAssignDescription
                    ? data.leasehold?.leaseholdClause?.consentToAssignDescription
                    : '',
                noticeOfAssignDescription: data.leasehold?.leaseholdClause?.noticeOfAssignDescription
                    ? data.leasehold?.leaseholdClause?.noticeOfAssignDescription
                    : '',
                poolingProvisionDescription: data.leasehold?.leaseholdClause?.poolingProvisionDescription
                    ? data.leasehold?.leaseholdClause?.poolingProvisionDescription
                    : '',
                otherDescription: data.leasehold?.leaseholdClause?.otherDescription ? data.leasehold?.leaseholdClause?.otherDescription : '',
            },

            leaseholdOwnerships:
                data?.leasehold?.leaseholdOwnerships?.length > 0 ? finalizeLeaseholdOwnerships(data.leasehold?.leaseholdOwnerships) : null,

            wiOwners: undefined,
        };
        data.leaseholds = data.leaseholds ? data.leaseholds : [];

        data.isCommitted = leaseData?.isCommitted;
        data.committedDate = leaseData?.committedDate;
        data.isInHand = leaseData?.isInHand;
        data.inHandDate = leaseData?.inHandDate;
        data.ownersAddressOnDocument = leaseData?.ownersAddressOnDocument;
        data.lesseeAddressLine1 = leaseData?.lesseeAddressLine1;
        data.lesseeCity = leaseData?.lesseeCity;
        data.lesseeState = leaseData?.lesseeState;
        data.lesseeZipCode = leaseData?.lesseeZipCode;
        data.lesseeOwnerLookupId = leaseData?.lesseeOwnerLookupId;
        data.tracts = leaseData?.tracts;
        data.clientFacingNotes = leaseData?.clientFacingNotes;
        data.isGeneratedLease = leaseData?.isGeneratedLease;
        data.isVoid = leaseData?.isVoid;
        return data;
    };

    const finalizeLeaseholdOwnerships = (leaseholdOwnerships: WIOwnership[]) => {
        leaseholdOwnerships.forEach((ownership) => {
            ownership.id = ownership.id ? parseInt(ownership.id + '', 10) : 0;
            ownership.projectId = ownership.projectId ? parseInt(ownership.projectId + '', 10) : 0;
            ownership.ownerLookUpId = ownership.ownerLookUpId ? parseInt(ownership.ownerLookUpId + '', 10) : 0;
            ownership.parentOwnershipId = ownership.parentOwnershipId ? parseInt(ownership.parentOwnershipId + '', 10) : 0;
            ownership.decimalInterest = ownership.decimalInterest ? parseFloat(ownership.decimalInterest + '') : 0;
            ownership.ownershipId = ownership.ownershipId ? parseInt(ownership.ownershipId + '', 10) : 0;
            ownership.leaseholdId = ownership.leaseholdId ? parseInt(ownership.leaseholdId + '', 10) : 0;
            ownership.note = ownership.note ? ownership.note : '';
            ownership.orriOwnerships = ownership.orriOwnerships ? finalizeLeaseholdOwnerships(ownership.orriOwnerships) : null;
        });

        return leaseholdOwnerships;
    };

    const parseBool = (value: string) => {
        if (value === 'true') {
            return true;
        } else if (value === 'false') {
            return false;
        }
        return null;
    };

    const previousLeasehold = () => {
        if (currentLeasehold > -1) {
            const leaseholdId = leaseData?.leaseHoldIds[currentLeasehold - 1];
            if (leaseholdId !== undefined) {
                setCurrentLeasehold(currentLeasehold - 1);
                dispatch(
                    push(
                        `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}/ownership/${appContext.ownershipId}/leases/${appContext.leaseId}/leaseholds/${leaseholdId}`
                    )
                );
            }
        }
    };

    const nextLeasehold = () => {
        if (currentLeasehold < leaseData?.leaseHoldIds?.length !== undefined ? leaseData?.leaseHoldIds?.length : 0) {
            const leaseholdId = leaseData?.leaseHoldIds?.[currentLeasehold + 1];

            if (leaseholdId !== undefined) {
                setCurrentLeasehold(currentLeasehold + 1);
                dispatch(
                    push(
                        `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}/ownership/${appContext.ownershipId}/leases/${appContext.leaseId}/leaseholds/${leaseholdId}`
                    )
                );
            }
        }
    };

    const onTryAddWI = () => {
        if (!isNaN(leaseData?.leaseholds?.[0]?.id)) {
            dispatch(actions.layout.showDialog(DialogList.ADD_WI_OWNERSHIP));
        } else {
            dispatch(actions.layout.showDialog(DialogList.LEASEHOLD_SAVE_BEFORE_ADDING_WI));
        }
    };

    const onAddORRI = (newParentOwnershipId: number) => {
        setParentOwnershipId(newParentOwnershipId);
        dispatch(actions.layout.showDialog(DialogList.ADD_ORRI_OWNERSHIP));
    };

    const onCopyORRI = (ownershipId: number) => {
        setParentOwnershipId(ownershipId);

        dialogContext.leasehold.cloneORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), () => location.reload());
    };

    const ownerNameForWorkingInterest = (workingInterestId: number) => {
        const wiOwners = PropertyUtil.SafeGet(() => leaseData?.leaseholds?.[0]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => leaseData?.leaseholds?.[0]?.leaseholdOwnerships);
        const ownership = wiOwnerships?.filter((wio: Ownership) => wio.id === workingInterestId)[0];
        const owner = wiOwners.filter((o: Owner) => o.ownerLookUpId === ownership.ownerLookUpId)[0];

        return owner?.name || '[Error]';
    };

    const onDeleteORRI = (ownershipId: number) => {
        setParentOwnershipId(ownershipId);
        const wiOwners = PropertyUtil.SafeGet(() => leaseData.leaseholds?.[0]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => leaseData.leaseholds?.[0]?.leaseholdOwnerships);
        const ownership: WIOwnership = wiOwnerships?.filter((wio: WIOwnership) => wio.id === ownershipId)[0];

        dialogContext.leasehold.deleteORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), ownership.orriOwnerships, wiOwners, () =>
            location.reload()
        );
    };

    const onDeleteLeaseholdOwnership = (leaseholdOwnershipId: number) => {
        dispatch(
            actions.leasehold.deleteLeaseholdOwnership(
                appContext.clientNumber,
                appContext.projectNumber,
                appContext.sectionNumber,
                appContext.tractNumber,
                appContext.ownershipId,
                leaseData?.leaseholds[0].id,
                leaseholdOwnershipId
            )
        );
        // this.props.deleteLeaseholdOwnership(this.clientNumber, this.projectNumber, this.sectionNumber, this.tractNumber, this.ownershipId, this.leaseholdId, leaseholdOwnershipId);
    };

    const openOwnershipNotes = (ownershipId: number) => {
        // this.setState({ ownershipNotesId: ownershipId });
        setSelectedWIOwnershipId(ownershipId);
        dialogContext.tract.openOwnershipNotes(findOwnership(ownershipId), leaseData?.leaseholds?.[0]?.wiOwners, '', 2, (ownership: Ownership) =>
            onUpdateWINote(ownership)
        );
    };

    const addDeviation = (values: AddOwner, containerOwnerId: number, ownerType: string) => {
        dispatch(actions.owner.addDeviation(appContext.clientNumber, appContext.projectNumber, values, containerOwnerId, ownerType));
    };

    const onGetTractsByOwnerId = async (id: number) => {
        const result = await get(
            `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leasegeneration/owner/${id}`
        );

        if (response.ok) {
            setOwnerTracts(result.tracts);
        }
    };

    const onSearchOwners = (searchString: string, ownerType: string) => {
        let searchOptions = new ODataOptions().addPaging(1, 50);
        if (searchString !== '') {
            searchOptions.addFilter(new ODataFilter('omni', ODataFilterOperators.Contains, searchString));
            searchOptions.addFilter(new ODataFilter('type', ODataFilterOperators.Equals, ownerType));
        } else {
            searchOptions.clearFilters();
        }
        initializeOwnerSearchDataOptions(searchOptions);
     };

    const onGetOwnerDetail = (ownerId: number, containerId: number, type: string) => {
        dispatch(actions.owner.getOwnerDetails(appContext.clientNumber, appContext.projectNumber, ownerId, containerId, type));
    };

    const onSaveAddOwnership = (values: AddWIOwnershipData) => {
        dispatch(
            actions.leasehold.saveAddOwnership(
                appContext.clientNumber,
                appContext.projectNumber,
                appContext.sectionNumber,
                appContext.tractNumber,
                appContext.ownershipId,
                leaseData?.leaseholds?.[0]?.id,
                values
            )
        );
    };

    const leaseholdList = () => {
        if (leaseholdIdData?.length > 0) {
            let leaseholdIds = leaseholdIdData?.map((lhId: number) => {
                return { value: lhId || 0, label: lhId?.toString() || 'Error' };
            });

            return leaseholdIds;
        } else {
            return [];
        }
    };

    const onCopyLeasehold = async () => {
        let copyLeaseholdId = getValues('sourceLeaseholdId');

        // tslint:disable-next-line: max-line-length
        const result = await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/ownership/${appContext.ownershipId}/leasehold/${appContext.leaseholdId}/copy-leasehold/${copyLeaseholdId}`);

        if (response.ok) {
            let currentPath = window.location.pathname.split('/');
            currentPath.splice(currentPath.length - 1, 1);
    
            dispatch(
                push(`${currentPath.join('/')}/${result}`)
            );
            getLeaseData(appContext.leaseId, currentLeasehold + 1);
        } else {
            window.alert('call failed');
        }
    };

    // Todo: Commented out for now, but needs work to delete a lease correctly
    // const showDeleteDialog = () => {
    //     dispatch(actions.layout.showDialog(DialogList.DELETE_LEASE));
    // };

    console.log('leaseData', leaseData);

    const warningStyle: React.CSSProperties = {
        display: 'inline-block',
        padding: '0.5rem',
        margin: '0 0 1rem 0',
        fontWeight: 700,
        background: '#d5d5d5',
        fontSize: '.8em',
        color: '#565656',
        width: '100%',
        textAlign: 'center',
        borderRadius: '0.4rem',
    };

    const findOwnership = (ownershipId: number = 0) => {
        let refId = ownershipId > 0 ? ownershipId : selectedWIOwnershipId;
        let ownership = leaseData?.leaseholds[0]?.leaseholdOwnerships?.find((lo: WIOwnership) => lo.id === refId);

        if (!ownership) {
            leaseData?.leaseholds[0]?.leaseholdOwnerships?.forEach((lo: WIOwnership, index: number) => {
                let tempOwnership = lo.orriOwnerships.find((wio: WIOwnership) => wio.id === refId);
                if (tempOwnership) {
                    ownership = tempOwnership;
                }
                console.log('f');
            });
        }

        return ownership;
    };

    const updateFiledStatus = () => {
        let ref = getValues();
        setIsMemo(ref.isFiledByMemo.toString() === 'true');
    };

    const deleteLeasehold = () => {
        const switchToLeaseholdId = leaseholdIdData?.find((x) => x !== leaseData?.leaseholds?.[0].id);
        const deleteId = leaseData?.leaseholds?.[0].id;
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 1, 1);

        const confirmAction = async () => {
            // tslint:disable-next-line: max-line-length
            await del(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/${appContext.tractNumber}/ownership/${appContext.ownershipId}/leasehold/${deleteId}/false`);
            if (response.ok) {
                getLeaseData(appContext.leaseId);
                dispatch(
                    replace(
                        `${currentPath.join('/')}/${switchToLeaseholdId}`
                    )
                );
            } else {
                window.alert('delete leasehold failed');
            }
            setCurrentLeasehold(0);
        };

        const body = (
            <>
                <div style={{ fontWeight: 'bold' }}>Are you sure you want to delete this Leasehold?</div>
            </>
        );

        dialogContext.common.confirmAction(`Delete Leasehold`, body, confirmAction);
    };

    const deleteLease = () => {
        const deleteId = leaseData?.leaseholds?.[0].id;
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 6, 6);

        const confirmAction = async () => {
            // tslint:disable-next-line: max-line-length
            await del(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/${appContext.tractNumber}/ownership/${appContext.ownershipId}/leasehold/${deleteId}/true`);
            if (response.ok) {
                dispatch(
                    replace(
                        `${currentPath.join('/')}`
                    )
                );
            } else {
                window.alert('delete leasehold failed');
            }
        };

        const body = (
            <>
                <div style={{ fontWeight: 'bold' }}>This is the only leasehold. The lease will also be deleted. Continue?</div>
            </>
        );

        dialogContext.common.confirmAction(`Delete Leasehold`, body, confirmAction);
    };

    const initializePage = () => {
        dispatch(actions.tract.initializeOwnerTracts());
        getLeaseData(appContext.leaseId, currentLeasehold + 1);
    };

    return (
        <div className="lease-info-page">
            <div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div>
                        {isDirty && (
                            <>
                                <div
                                    id="dirtyFlag"
                                    style={{
                                        position: 'fixed',
                                        top: '4.7rem',
                                        left: '0',
                                        width: '100%',
                                        color: 'white',
                                        backgroundColor: 'rgba(255,0,0,.5)',
                                        zIndex: 9000,
                                        padding: '1rem 0',
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <div>Unsaved Changes - Please Save Before Leaving Forms</div>
                                    <div style={{ position: 'absolute', right: '.5rem', top: '50%', transform: 'translateY(-50%)', display: 'flex' }}>
                                        <div>
                                            <Button text="Save" type="submit" size="small" intent="primary" />
                                        </div>
                                        <div style={{ margin: '0 1rem', borderRight: '1px solid white', width: '1px' }} />
                                        <Button text="Cancel Changes" size="small" onClick={() => reset()} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <RHFLegend title="Lease Information" />
                    {/* 
                        <div style={{ display: 'flex', flexDirection: 'row-reverse', width: '100%' }}>
                            <div
                                style={{ color: 'red', fontWeight: 'bold', textDecorationLine: 'underline' }}
                                className="clickable"
                                onClick={() => showDeleteDialog()}
                            >
                                <span>Delete Lease</span>
                            </div>
                        </div> 
                    */}

                    <div className="form-row">
                        <RHFInputBox label="Lease Number" name="leaseNumber" register={register} type="text" errors={errors} disabled={true} />
                        <RHFInputBox
                            label="Lease Date"
                            name="leaseDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                        />
                        <RHFInputBox
                            label="Effective Date"
                            name="effectiveDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                            onChange={handleExpirationChange}
                        />
                        <RHFInputBox label="Volume" name="volume" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Page" name="page" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Inst. Number" name="instrumentNumber" register={register} type="text" errors={errors} />
                        <RHFInputBox
                            label="Recorded Date"
                            name="recordedDate"
                            register={register}
                            type="date"
                            errors={errors}
                        />
                        <RHFInputBox label="Fed / State Lease #" name="fedStateLeaseNumber" register={register} type="text" errors={errors} />
                        <RHFToggle label="Filed by Memorandum" name="isFiledByMemo" register={register} errors={errors} onChange={() => updateFiledStatus()} />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Original Gross Acres"
                            name="originalGross"
                            register={register}
                            type="number"
                            errors={errors}
                            step={0.0001}
                        />

                        <RHFInputBox label="Original Lessor" name="ownersOnDocument" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Original Lessee" name="lesseeName" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Lessor AKA" name="leasehold.lessorAKA" register={register} type="text" errors={errors} />
                    </div>

                    <RHFLegend title="Leasehold" />
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                        <div className="form-row">
                            <Button text="Previous" onClick={previousLeasehold} size="small" intent="primary" />
                            {/* <span onClick={previousLeasehold} style={{ border: '1px solid black' }} className="clickable">Previous</span> */}
                            <span style={{ padding: '.5rem' }}>{`${currentLeasehold + 1} of ${leaseData?.leaseHoldIds?.length || 1}`}</span>
                            <Button text="Next" onClick={nextLeasehold} size="small" intent="primary" />
                            {/* <span onClick={nextLeasehold} style={{ border: '1px solid black' }} className="clickable">Next</span> */}
                            {leaseData?.leaseholds?.[0]?.mineralOwner && (
                                <div style={{ marginLeft: '2rem', fontSize: '1.5rem' }}>
                                    <span>Owner: </span>
                                    <span>{OwnerUtil.MapOwner(leaseData?.leaseholds?.[0]?.mineralOwner)?.ownerName() || 'N/A'}</span>
                                </div>
                            )}
                        </div>
                        {leaseholdIdData?.length >= 1 && (
                            <div style={{ display: 'flex' }}>
                                {copyLeaseholdClicked && (
                                    <div style={{textAlign: 'center', marginRight: '1rem'}}>
                                        <RHFSelect 
                                            onChange={(e) => handleSourceSelected(e)} 
                                            name="sourceLeaseholdId" 
                                            label="Source Leasehold" 
                                            options={leaseholdList()} 
                                            register={register} 
                                        />
                                        <Button 
                                            style={{marginTop: '1rem'}}
                                            disabled={!sourceSelected} 
                                            text="Confirm Copy"
                                            intent="danger" 
                                            onClick={onCopyLeasehold} 
                                        />
                                    </div>
                                )}
                                <div>
                                    {appContext.leaseholdId === 0 && (
                                        <Button 
                                            text="Copy Leasehold" 
                                            intent="primary" 
                                            onClick={() => setCopyLeaseholdClicked(true)} 
                                        />
                                    )}
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Leasehold"
                                        onClick={() => deleteLeasehold()}
                                        intent="danger"
                                    />
                                </div>
                            </div>
                        )}
                        {leaseholdIdData?.length === 1 && (
                            <div style={{ display: 'flex' }}>
                                <div>
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Lease"
                                        onClick={() => deleteLease()}
                                        intent="danger"
                                    />
                                </div>
                            </div>                        
                        )}
                    </div>
                    <div>
                        <div>
                            <RHFCheckbox 
                                style={{marginBottom: '2rem'}}
                                label={'Active Lease'} 
                                name={'leasehold.isActiveLease'} 
                                register={register} 
                                errors={errors} 
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            isRequired={true}
                            rules={{ required: 'field required' }}
                            label="Primary Term"
                            name="leasehold.primaryTerm"
                            register={register}
                            type="number"
                            errors={errors}
                            onChange={handleExpirationChange}
                        />
                        <RHFSelect
                            name="leasehold.primaryTermUnits"
                            label="Term Unit"
                            options={[
                                { value: 'years', label: 'Years' },
                                { value: 'months', label: 'Months' },
                                { value: 'days', label: 'Days' },
                            ]}
                            onChange={handleExpirationChange}
                            register={register}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                            errors={errors}
                        />
                        {/* <RHFInputBox label="Term Unit" name="leasehold.primaryTermUnits" register={register} type="text" errors={errors} /> */}
                        <RHFInputBox label="Expiration Date" name="leasehold.expirationDate" register={register} type="date" errors={errors} />
                        <RHFInputBox
                            label="Royalty"
                            name="leasehold.royalty"
                            register={register}
                            onChange={(e) => setRoyalty(parseFloat(e.currentTarget.value))}
                            type="text"
                            rules={{ required: isMemo ? false : 'field required' }}
                            isRequired={!isMemo}
                            errors={errors}
                        />
                    </div>

                    <div className="form-row">
                        <RHFToggle label="Has Option to Extend" name="leasehold.renewalOption" register={register} errors={errors} />

                        {/* <RHFInputBox label="Renewal Option" name="leasehold.renewalOption" register={register} type="text" errors={errors} /> */}
                        <RHFInputBox label="Option Term" name="leasehold.optionTerm" register={register} type="text" errors={errors} />
                        <RHFSelect
                            name="leasehold.optionTermUnits"
                            label="Option Term Unit"
                            options={[
                                { value: 'years', label: 'Years' },
                                { value: 'months', label: 'Months' },
                                { value: 'days', label: 'Days' },
                            ]}
                            register={register}
                            errors={errors}
                        />
                        {/* <RHFInputBox label="Term Units" name="leasehold.optionTermUnits" register={register} type="text" errors={errors} /> */}
                        {/* <RHFInputBox label="Price" name="leasehold.optionPricePerAcre" register={register} type="text" errors={errors} /> */}
                        <RHFToggle label="Option Exercised" name="leasehold.optionExercised" register={register} errors={errors} />
                        {/* <RHFInputBox label="Option Exercised" name="leasehold.optionExercised" register={register} type="text" errors={errors} /> */}
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            name="bonusAmount"
                            label="Bonus Amount"
                            register={register}
                            errors={errors}
                        />
                        <RHFSelect
                            name="bonusUnits"
                            label="Bonus Unit"
                            options={[
                                { value: 'flatFee', label: 'Flat Fee' },
                                { value: 'perAcre', label: 'Per Acre' },
                            ]}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            name="totalBonus"
                            label="Total Bonus"
                            register={register}
                            errors={errors}
                        />
                    </div>

                    <div className="form-row">
                        {/* conditional render based on option checkbox */}
                        <RHFInputBox
                            type="number"
                            name="leasehold.optionBonusAmount"
                            label="Option Bonus Amount"
                            register={register}
                            errors={errors}
                        />
                        <RHFSelect
                            name="leasehold.optionBonusAmountUnits"
                            label="Option Bonus Unit"
                            options={[
                                { value: 'flatFee', label: 'Flat Fee' },
                                { value: 'perAcre', label: 'Per Acre' },
                            ]}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            type="number"
                            name="leasehold.optionTotalBonus"
                            label="Total Option Bonus"
                            register={register}
                            errors={errors}
                        />
                        {/* <RHFInputBox name="leasehold.optionTerm" label="Option Term" register={register} errors={errors} />
                        <RHFSelect
                            name="leasehold.optionTermUnits"
                            label="Option Term Unit"
                            options={[
                                { value: 'years', label: 'Years' },
                                { value: 'months', label: 'Months' },
                                { value: 'days', label: 'Days' },
                            ]}
                            register={register}
                            errors={errors}
                        /> */}
                    </div>

                    <RHFLegend title="Original Legal Description" />
                    <div className="form-row">
                        <div className="form-field" style={{ width: '100%' }}>
                            <textarea
                                name="legalDescription"
                                ref={register}
                                style={{
                                    width: '100%',
                                    height: '6rem',
                                    border: '0',
                                    borderBottom: '1px solid black',
                                    resize: 'none',
                                    overflowY: 'auto',
                                }}
                            />
                        </div>
                    </div>

                    <RHFLegend title="Notes" />

                    <div className="form-row" style={{ width: '100%' }}>
                        <div className="form-field" style={{ width: '100%' }}>
                            <textarea
                                name="leasehold.notes"
                                ref={register}
                                style={{
                                    width: '100%',
                                    height: '6rem',
                                    border: '0',
                                    borderBottom: '1px solid black',
                                    resize: 'none',
                                    overflowY: 'auto',
                                }}
                            />
                        </div>
                    </div>

                    <RHFLegend title="Pooling Information" />

                    <div className="form-row">
                        <RHFToggle label="HBP" name="leasehold.hbp" register={register} errors={errors} />
                        <RHFInputBox
                            label="Pooling Order Number"
                            name="leasehold.poolingOrderNumber"
                            register={register}
                            type="text"
                            errors={errors}
                        />
                        <RHFInputBox label="Operator" name="leasehold.operator" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Well Name" name="leasehold.wellName" register={register} type="text" errors={errors} />
                        <RHFToggle label="HBU" name="leasehold.hbu" register={register} errors={errors} />
                        <RHFInputBox label="API #" name="leasehold.apiNumber" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Unit Name" name="leasehold.unitName" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Field Name" name="leasehold.fieldName" register={register} type="text" errors={errors} />
                    </div>

                    <RHFLegend title="Lease Clauses" />

                    <div className="form-row">
                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.depthPugh"
                            label="Depth Pugh"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />

                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.pugh"
                            label="Pugh"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />

                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.depthExclusion"
                            label="Depth Exclusion"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />

                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.continuingDevelopment"
                            label="Continuing Dev"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />

                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.shutIn"
                            label="Shut In"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />

                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.surfaceRestriction"
                            label="Surface Restriction"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />

                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.favoredNation"
                            label="Favored Nation"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />

                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.nonDrilling"
                            label="Non Drilling"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />
                    </div>

                    <div className="form-row">
                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.topLeaseLanguage"
                            label="Top Lease Language"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />

                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.netProceedsLease"
                            label="Net Proceeds Lease"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />

                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.costFreeRoyalty"
                            label="Cost Free Royalty"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />

                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.consentToAssign"
                            label="Consent To Assign"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />

                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.noticeOfAssign"
                            label="Notice Of Assign"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />

                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.poolingProvision"
                            label="Pooling Provision"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />

                        <RHFLeaseClause
                            name="leasehold.leaseholdClause.other"
                            label="Other"
                            descriptionRequired={true}
                            register={register}
                            errors={errors}
                        />
                    </div>

                    <RHFLegend title="Document Management" />
                    {leaseData?.leaseholds && (
                        <LeaseholdDocs
                            showDialog={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            hideDialog={() => dispatch(actions.layout.hideDialog())}
                            leasehold={leaseData?.leaseholds?.[0]}
                        />
                    )}

                    <RHFLegend title="Leasehold Ownership" />
                    <span style={warningStyle}>ALL INTEREST REPRESENTED AS DECIMAL</span>
                    {/* <div className="form-row" style={{ width: '100%' }}>
                        <WorkingInterestList
                            isGovStateBlm={false}
                            owners={leaseData?.leaseholds?.[0]?.wiOwners}
                            wiOwnerships={leaseData?.leaseholds?.[0]?.leaseholdOwnerships}
                            setDialogShowing={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            tryAddWI={onTryAddWI}
                            onAddORRI={onAddORRI}
                            onCopyORRI={onCopyORRI}
                            onDeleteORRI={onDeleteORRI}
                            deleteLeaseholdOwnership={onDeleteLeaseholdOwnership}
                            totalWI={'100'}
                            openOwnershipNotes={openOwnershipNotes}
                        />
                    </div> */}
                    <div style={{ minHeight: '20rem' }}>
                        <WorkingInterestList
                            isGovStateBlm={false}
                            owners={leaseData?.leaseholds?.[0]?.wiOwners}
                            wiOwnerships={leaseData?.leaseholds?.[0]?.leaseholdOwnerships}
                            setDialogShowing={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            tryAddWI={onTryAddWI}
                            onAddORRI={onAddORRI}
                            onCopyORRI={onCopyORRI}
                            onDeleteORRI={onDeleteORRI}
                            deleteLeaseholdOwnership={onDeleteLeaseholdOwnership}
                            openOwnershipNotes={openOwnershipNotes}
                            register={register}
                            errors={errors}
                            getValues={getValues}
                            setValue={setValue}
                            watch={watch}
                            royalty={royalty}
                        />
                    </div>

                    <div>
                        <Button text="Save" type="submit" intent="primary" />
                        {/* <input type="submit" /> */}
                        {/* <input type="reset" onClick={() => { clearErrors(); formState.isValid = true; }} /> */}
                    </div>
                </form>
            </div>

            <AddOwnership
                title={'Add Working Interest Ownership'}
                dialogName={DialogList.ADD_WI_OWNERSHIP}
                addDeviation={addDeviation}
                uspsCityState={tract.uspsCityState}
                getCityState={(zipCode: number) => dispatch(actions.tract.getCityState(zipCode))}
                initializeOwnerTracts={() => initializePage()}
                clearSearchResults={() => dispatch(actions.tract.clearSearchResults())}
                getTractsByOwnerId={onGetTractsByOwnerId}
                ownerTracts={ownerTracts}
                clientNumber={appContext.clientNumber}
                projectNumber={appContext.projectNumber}
                hideDialog={() => dispatch(actions.layout.hideDialog())}
                getStates={(options: ODataOptions) => dispatch(actions.state.getStates(options))}
                states={selectState.states}
                navigate={(path: string) => dispatch(push(path))}
                searchOwners={onSearchOwners}
                getOwnerDetail={onGetOwnerDetail}
                saveAddLeaseOwnership={onSaveAddOwnership}
                searchResults={getNewOwnerList()}
                ownerDetails={selectOwner.ownerDetails}
                getPhoneNumberTypes={() => dispatch(actions.phoneNumberType.getPhoneNumberTypes())}
                phoneNumberTypes={selectPhoneNumberTypes.phoneNumberTypes}
                getScrollStatus={getScrollStatus}
            />

            <AddOwnership
                title="Add Overriding Royalty Interest Ownership"
                dialogName={DialogList.ADD_ORRI_OWNERSHIP}
                addDeviation={addDeviation}
                uspsCityState={tract.uspsCityState}
                getCityState={(zipCode: number) => dispatch(actions.tract.getCityState(zipCode))}
                initializeOwnerTracts={() => initializePage()}
                clearSearchResults={() => dispatch(actions.tract.clearSearchResults())}
                getTractsByOwnerId={onGetTractsByOwnerId}
                ownerTracts={ownerTracts}
                clientNumber={appContext.clientNumber}
                projectNumber={appContext.projectNumber}
                hideDialog={() => dispatch(actions.layout.hideDialog())}
                getStates={(options: ODataOptions) => dispatch(actions.state.getStates(options))}
                states={selectState.states}
                navigate={(path: string) => dispatch(push(path))}
                searchOwners={onSearchOwners}
                getOwnerDetail={onGetOwnerDetail}
                saveAddLeaseOwnership={onSaveAddOwnership}
                searchResults={getNewOwnerList()}
                ownerDetails={selectOwner.ownerDetails}
                getPhoneNumberTypes={() => dispatch(actions.phoneNumberType.getPhoneNumberTypes())}
                phoneNumberTypes={selectPhoneNumberTypes.phoneNumberTypes}
                parentOwnershipId={parentOwnershipId}
                getScrollStatus={getScrollStatus}
            />
            <LeaseInfoDeleteDialog onDelete={() => deleteLeaseData()} />
        </div>
    );
};

export default LeaseInfo;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/LeaseInfo.redesign.tsx